import { useState } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function RecommendationsManager({ recommendations, onRefresh, headers, categories = ['book', 'tool', 'resource'], onCategoriesChange }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [formData, setFormData] = useState({ title: '', category: categories[0] || 'book', description: '', link: '', image_url: '', published: false, expires_at: '' });
    const [newCategory, setNewCategory] = useState('');
    const [showCategoryInput, setShowCategoryInput] = useState(false);

    const handleSave = async () => {
        try {
            const dataToSend = { ...formData };
            if (!dataToSend.expires_at) {
                dataToSend.expires_at = null;
            }
            
            if (editItem) {
                await axios.put(`${API}/recommendations/${editItem.id}`, dataToSend, { headers });
                toast.success('Recommendation updated!');
            } else {
                await axios.post(`${API}/recommendations`, dataToSend, { headers });
                toast.success('Recommendation created!');
            }
            setDialogOpen(false);
            setEditItem(null);
            setFormData({ title: '', category: categories[0] || 'book', description: '', link: '', image_url: '', published: false, expires_at: '' });
            onRefresh();
        } catch (error) {
            toast.error('Failed to save');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this recommendation?')) return;
        try {
            await axios.delete(`${API}/recommendations/${id}`, { headers });
            toast.success('Recommendation deleted!');
            onRefresh();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const openEdit = (item) => {
        setEditItem(item);
        setFormData({ 
            title: item.title, 
            category: item.category, 
            description: item.description, 
            link: item.link, 
            image_url: item.image_url, 
            published: item.published,
            expires_at: item.expires_at || ''
        });
        setDialogOpen(true);
    };

    const handleAddCategory = async () => {
        if (!newCategory.trim()) return;
        const newCat = newCategory.trim().toLowerCase();
        if (categories.includes(newCat)) {
            toast.error('Category already exists');
            return;
        }
        const updatedCategories = [...categories, newCat];
        try {
            await axios.put(`${API}/settings`, { recommendation_categories: updatedCategories }, { headers });
            toast.success('Category added!');
            setNewCategory('');
            setShowCategoryInput(false);
            if (onCategoriesChange) onCategoriesChange();
        } catch (error) {
            toast.error('Failed to add category');
        }
    };

    const handleDeleteCategory = async (catToDelete) => {
        if (!window.confirm(`Delete category "${catToDelete}"? Recommendations with this category will keep their current category.`)) return;
        const updatedCategories = categories.filter(c => c !== catToDelete);
        try {
            await axios.put(`${API}/settings`, { recommendation_categories: updatedCategories }, { headers });
            toast.success('Category deleted!');
            if (onCategoriesChange) onCategoriesChange();
        } catch (error) {
            toast.error('Failed to delete category');
        }
    };

    const formatExpiryDate = (dateString) => {
        if (!dateString) return 'Never';
        const date = new Date(dateString);
        if (date < new Date()) return 'Expired';
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    return (
        <div className="retro-card p-6" data-testid="recommendations-manager">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg uppercase tracking-tight" style={{ fontFamily: "'Silkscreen', cursive" }}>
                    Manage Recommendations
                </h2>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="rounded-none border-2 border-primary bg-primary text-primary-foreground retro-btn text-xs uppercase tracking-widest" data-testid="add-recommendation-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Add Recommendation
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-none border-2 max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="uppercase tracking-tight" style={{ fontFamily: "'Silkscreen', cursive" }}>
                                {editItem ? 'Edit Recommendation' : 'New Recommendation'}
                            </DialogTitle>
                            <DialogDescription>Fill in the details below</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-xs uppercase tracking-widest">Title</Label>
                                <Input className="rounded-none border-2 mt-1" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} data-testid="recommendation-title-input" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest">Category</Label>
                                <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                                    <SelectTrigger className="rounded-none border-2 mt-1" data-testid="recommendation-category-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-none border-2">
                                        {categories.map(cat => (
                                            <SelectItem key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest">Description</Label>
                                <Textarea className="rounded-none border-2 mt-1" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} data-testid="recommendation-description-input" />
                            </div>
                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <Label className="text-xs uppercase tracking-widest">Link</Label>
                                    <Input className="rounded-none border-2 mt-1" value={formData.link} onChange={(e) => setFormData({...formData, link: e.target.value})} />
                                </div>
                                <div className="flex-1">
                                    <Label className="text-xs uppercase tracking-widest">Image URL</Label>
                                    <Input className="rounded-none border-2 mt-1" value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest">Auto-hide after (leave empty for never)</Label>
                                <Input 
                                    type="datetime-local" 
                                    className="rounded-none border-2 mt-1" 
                                    value={formData.expires_at ? formData.expires_at.slice(0, 16) : ''} 
                                    onChange={(e) => setFormData({...formData, expires_at: e.target.value ? new Date(e.target.value).toISOString() : ''})} 
                                    data-testid="recommendation-expires-input"
                                />
                                <p className="text-xs text-muted-foreground mt-1">Recommendation will be hidden from landing page after this date</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch checked={formData.published} onCheckedChange={(checked) => setFormData({...formData, published: checked})} data-testid="recommendation-published-switch" />
                                <Label className="text-xs uppercase tracking-widest">Published</Label>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => { setDialogOpen(false); setEditItem(null); }} className="rounded-none border-2">Cancel</Button>
                            <Button onClick={handleSave} className="rounded-none border-2 border-primary bg-primary text-primary-foreground" data-testid="save-recommendation-btn">Save</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Category Management */}
            <div className="mb-6 p-4 border-2 border-dashed border-border">
                <div className="flex items-center justify-between mb-3">
                    <Label className="text-xs uppercase tracking-widest">Categories</Label>
                    {!showCategoryInput && (
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setShowCategoryInput(true)}
                            className="rounded-none border-2 text-xs"
                        >
                            <Plus className="h-3 w-3 mr-1" /> Add Category
                        </Button>
                    )}
                </div>
                <div className="flex flex-wrap gap-2">
                    {categories.map(cat => (
                        <div key={cat} className="flex items-center gap-1 px-3 py-1 bg-muted border-2 border-border">
                            <span className="text-xs uppercase tracking-widest">{cat}</span>
                            {categories.length > 1 && (
                                <button 
                                    onClick={() => handleDeleteCategory(cat)}
                                    className="ml-1 text-muted-foreground hover:text-destructive"
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
                {showCategoryInput && (
                    <div className="flex gap-2 mt-3">
                        <Input 
                            className="rounded-none border-2 flex-1" 
                            value={newCategory} 
                            onChange={(e) => setNewCategory(e.target.value)}
                            placeholder="New category name"
                            onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                        />
                        <Button onClick={handleAddCategory} className="rounded-none border-2" size="sm">Add</Button>
                        <Button variant="outline" onClick={() => { setShowCategoryInput(false); setNewCategory(''); }} className="rounded-none border-2" size="sm">Cancel</Button>
                    </div>
                )}
            </div>

            {recommendations.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No recommendations yet. Share what you love!</p>
            ) : (
                <div className="space-y-4">
                    {recommendations.map(item => (
                        <div key={item.id} className="flex items-center justify-between p-4 border-2 border-border" data-testid={`recommendation-row-${item.id}`}>
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs uppercase tracking-widest px-2 py-0.5 bg-muted">{item.category}</span>
                                    <h3 className="font-bold">{item.title}</h3>
                                </div>
                                <p className="text-sm text-muted-foreground">{item.description}</p>
                                {item.expires_at && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Auto-hides: {formatExpiryDate(item.expires_at)}
                                    </p>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {item.published ? (
                                    <span className="text-xs uppercase tracking-widest text-green-500 flex items-center gap-1"><Check className="h-3 w-3" /> Live</span>
                                ) : (
                                    <span className="text-xs uppercase tracking-widest text-muted-foreground">Draft</span>
                                )}
                                <Button variant="outline" size="icon" onClick={() => openEdit(item)} className="rounded-none border-2" data-testid={`edit-recommendation-${item.id}`}><Edit className="h-4 w-4" /></Button>
                                <Button variant="outline" size="icon" onClick={() => handleDelete(item.id)} className="rounded-none border-2 text-destructive" data-testid={`delete-recommendation-${item.id}`}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
